import React, { Fragment, type PropsWithChildren } from 'react';
import Checkbox from '@atlaskit/checkbox';
import { Inline } from '@atlaskit/primitives/compiled';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	CheckboxCell as CheckboxCellComponent,
	HeaderCheckboxCell as HeaderCheckboxCellComponent,
} from './main.tsx';
import { messages } from './messages.tsx';
import type { CheckboxCellProps, HeaderCheckboxCellProps } from './types.tsx';

const MaybeCheckboxUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="checkbox-cell" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

const MaybeHeaderCheckboxUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="header-checkbox-cell" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

export const CheckboxCell = ({ issueId, ...checkboxProps }: CheckboxCellProps) => {
	const { formatMessage } = useIntl();
	return __SERVER__ ? (
		<MaybeCheckboxUFOSegment>
			<Inline alignInline="center" data-vc="checkbox-cell">
				<Checkbox
					aria-label={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.checkboxAriaLabelIssueTermRefresh
							: messages.checkboxAriaLabel,
					)}
					onClick={() => abortAll('new_interaction')}
					{...checkboxProps}
				/>
			</Inline>
		</MaybeCheckboxUFOSegment>
	) : (
		<MaybeCheckboxUFOSegment>
			<CheckboxCellComponent issueId={issueId} {...checkboxProps} />
		</MaybeCheckboxUFOSegment>
	);
};

export const HeaderCheckboxCell = ({ ...checkboxProps }: HeaderCheckboxCellProps) => {
	const { formatMessage } = useIntl();
	return __SERVER__ ? (
		<MaybeHeaderCheckboxUFOSegment>
			<Inline alignInline="center" data-vc="header-checkbox-cell">
				<Checkbox
					aria-label={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.checkboxAriaLabelIssueTermRefresh
							: messages.checkboxAriaLabel,
					)}
					onClick={() => abortAll('new_interaction')}
					{...checkboxProps}
				/>
			</Inline>
		</MaybeHeaderCheckboxUFOSegment>
	) : (
		<MaybeHeaderCheckboxUFOSegment>
			<HeaderCheckboxCellComponent {...checkboxProps} />
		</MaybeHeaderCheckboxUFOSegment>
	);
};
